import React, { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    TableSortLabel,
    Typography
} from "@mui/material";
import { columns } from "../../../dtos/columns";


interface TablaProps<T> {
    titulo?: React.ReactNode;
    columns: columns<T>[];
    data: T[];
    showFooter?: boolean;
}

const Tabla = <T,>({ titulo, columns, data, showFooter = true }: TablaProps<T>) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState<"asc" | "desc">("asc");
    const [orderBy, setOrderBy] = useState<keyof T | null>(null);

    const handleSort = (property: keyof T) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Función para ordenar los datos
    const sortedData = useMemo(() => {
        if (!Array.isArray(data)) return [];
        if (!orderBy) return data;
        return [...data].sort((a, b) => {
            const valueA = a[orderBy] as any;
            const valueB = b[orderBy] as any;
            return (valueA < valueB ? -1 : 1) * (order === "asc" ? 1 : -1);
        });
    }, [data, orderBy, order]);

    return (
        <Paper sx={{ width: "100%", overflow: "hidden" }}>
            {titulo}
            <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
                <Table sx={{ tableLayout: "auto", width: "100%" }}>
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableRow>
                            {columns.map((column) =>
                                !column.omit ? (
                                    <TableCell
                                        key={column.name as string}
                                        align={column.alling || "left"}
                                        width={column.width || "auto"}
                                        sx={{ minWidth: column.width || 120, paddingX: 2, paddingY: 2 }}
                                    >
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === column.sortField}
                                                direction={orderBy === column.sortField ? order : "asc"}
                                                onClick={() => handleSort(column.sortField as keyof T)}
                                            >
                                                {column.name}
                                            </TableSortLabel>
                                        ) : (
                                            column.name
                                        )}
                                    </TableCell>
                                ) : null
                            )}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedData.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    No se ha encontrado data
                                </TableCell>
                            </TableRow>
                        ) : (
                            sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {columns.map((column) =>
                                        !column.omit ? (
                                            <TableCell
                                                key={column.name as string }
                                                align={column.alling || "left"}
                                                sx={{
                                                    whiteSpace: column.wrap ? "normal" : "nowrap",
                                                    minWidth: column.width || 120,
                                                    paddingX: 2,
                                                    paddingY: 2,
                                                }}
                                            >
                                                {column.cell ? column.cell(row) : column.selector(row)}
                                            </TableCell>
                                        ) : null
                                    )}
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            { showFooter && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, { label: "Todos", value: data.length }]}
                    component="div"
                    count={data.length}
                    rowsPerPage={rowsPerPage === -1 ? data.length : rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={<Typography variant="body2" sx={{ mt:0 }}>Filas por página</Typography>}
                    labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
                        `${from}-${to} de ${count}`
                    } slotProps={{
                        displayedRows: {
                            sx: { mt: 2 }
                        }
                    }}
                />
            ) }
        </Paper>
    );
};

export default Tabla;
